import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ScoreBadge, BranchPill, TagChip } from '../components/Badges';
import DateModeSelector from '../components/DateModeSelector';
import { fmtRelative, SOURCE_LABELS } from '../lib/constants';
import {
  Search, Plus, RefreshCcw, Trash2, AlertTriangle, X, Download,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CalendarRange, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import { API_BASE } from '../lib/api';
// ★ 2026-08-30 (code review) — sanificazione HTML i18n renderizzato
import { sanitizeHtml } from '../lib/sanitize';
import { getToken } from '../lib/tokenStorage';
import { CrmFullExportButton } from '../components/leads/CrmFullExportButton';

// ★ 2026-08-02 — Solo questi 4 utenti possono scaricare il CSV contatti dalla
// pagina Lead (Morena, Claudia, Richard, Eufemia).
const PRIORITY_EXPORT_EMAILS = new Set([
  'legal@housecashflows.com',     // Richard
  'morena@housecashflows.com',
  'claudia@housecashflows.com',
  'eufemia@housecashflows.com',
]);

// ★ 2026-07-03: paginazione lato server — 50 lead per pagina
const PAGE_SIZE = 50;

export default function LeadsPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth() || {};
  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);
  // ★ 2026-08-02 — solo Morena/Claudia/Richard/Eufemia vedono l'export CSV.
  const canExportCsv = PRIORITY_EXPORT_EMAILS.has((user?.email || '').toLowerCase());
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // ★ 2026-07-03: pre-fill agent_id if coming from AgentDetailPage
  const initialAgentId = searchParams.get('agent_id') || '';
  const initialSeniorId = searchParams.get('senior_id') || '';
  // ★ 2026-08-02: supporta stage multipli via URL (?stage=show,proposta_inviata)
  const initialStageParam = searchParams.get('stage') || '';
  const initialStages = initialStageParam
    ? initialStageParam.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const [agentFilter, setAgentFilter] = useState(initialAgentId);
  const [seniorFilter, setSeniorFilter] = useState(initialSeniorId);
  const [filters, setFilters] = useState({
    branch: '',
    source: '',
    stages: initialStages,   // ★ 2026-08-02: multi-select stage (array di key)
    search: '',
    startDate: '',
    endDate: '',
  });
  const [exportingCsv, setExportingCsv] = useState(false);
  const [page, setPage] = useState(1);  // 1-indexed for UX friendliness
  // ★ 2026-07-30 — date_mode toggle. `date_field` internal name matches the
  // /api/leads query param: 'created_at' | 'activity'.
  const [dateMode, setDateMode] = useState(() => {
    try {
      const v = localStorage.getItem('gp_dash_date_mode');
      return v === 'origin' || v === 'activity' ? v : 'activity';
    } catch { return 'activity'; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('gp_dash_date_mode', dateMode);
    } catch (err) {
      // Modalità privata / quota exceeded: non blocchiamo l'UI.
       
      logger.debug('[LeadsPage] localStorage.setItem failed:', err?.message);
    }
  }, [dateMode]);

  // ★ 2026-07-29: dynamic stage list from /api/pipeline/config (17 stage v2.1)
  const [pipelineStages, setPipelineStages] = useState([]);
  const [seniorAgents, setSeniorAgents] = useState([]);

  useEffect(() => {
    // Load stages once (used by dropdown)
    api.get('/pipeline/config').then((r) => {
      setPipelineStages(r.data?.stages || []);
    }).catch(() => {});
    // Load senior agents for filter dropdown
    api.get('/agents').then((r) => {
      const seniors = (r.data || []).filter((a) =>
        a.is_active !== false && (
          a.is_senior === true
          || a.is_ceo_fallback === true
          || (a.role_label || '').toLowerCase().includes('senior')
          || ['ceo', 'co_ceo', 'senior_associate'].includes(a.role || '')
        )
      );
      setSeniorAgents(seniors);
    }).catch(() => setSeniorAgents([]));
  }, []);

  // ─── Bulk-delete selection state (admin only) ────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === leads.length) return new Set();
      return new Set(leads.map((l) => l.id));
    });
  }, [leads]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.branch) params.set('branch', filters.branch);
    if (filters.source) params.set('source', filters.source);
    // ★ 2026-08-02 — stage multi-select: passa lista comma-separated al backend.
    if (filters.stages && filters.stages.length > 0) {
      params.set('stage', filters.stages.join(','));
    }
    if (filters.search) params.set('search', filters.search);
    if (filters.startDate) params.set('start_date', filters.startDate);
    if (filters.endDate) params.set('end_date', filters.endDate);
    // ★ 2026-07-30 — pass the date_field matching the selected mode so
    // Dashboard and Leads page count the same set of leads.
    if (filters.startDate || filters.endDate) {
      params.set('date_field', dateMode === 'origin' ? 'created_at' : 'activity');
    }
    if (agentFilter) params.set('agent_id', agentFilter);
    if (seniorFilter) params.set('senior_id', seniorFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String((page - 1) * PAGE_SIZE));
    params.set('include_total', 'true');
    try {
      const r = await api.get(`/leads?${params.toString()}`);
      setLeads(r.data);
      const totalHeader = r.headers?.['x-total-count'];
      if (totalHeader !== undefined) setTotal(parseInt(totalHeader, 10) || 0);
      setSelectedIds(new Set());  // reset selection on reload
    } catch (err) {
      logger.error('load leads failed', err);
      toast.error(t('leads.loadError', 'Errore caricamento lead'));
    } finally {
      setLoading(false);
    }
  }, [
    filters.branch, filters.source, filters.stages, filters.search,
    filters.startDate, filters.endDate, agentFilter, seniorFilter, page, dateMode, t,
  ]);

  // ★ 2026-07-29 (hooks fix): filter changes are applied via the "Applica"
  // button (see `applyFilters` below), NOT automatically on every keystroke.
  // We deliberately omit `load` (and its filter deps) from this effect so we
  // only re-fetch when the current page changes or on initial mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page]);

  // ★ 2026-08-14 (Fase 54) — Real-time sync ogni 15s + refresh on visibility.
  // Se Richard modifica un lead, gli altri associati vedono la modifica in ≤ 15s.
  // Refresh anche quando l'utente torna sulla scheda dopo essere stato altrove.
   
  useEffect(() => {
    const REFRESH_MS = 15000;
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
    // ★ 2026-08-30 (code review) — `load` nelle deps: l'interval si ricrea
    // quando cambiano i filtri e richiama sempre la versione aggiornata.
  }, [load]);

  const applyFilters = () => {
    if (page !== 1) {
      setPage(1);  // will trigger load via useEffect(page)
    } else {
      load();
    }
  };

  const resetFilters = () => {
    setFilters({ branch: '', source: '', stages: [], search: '', startDate: '', endDate: '' });
    setAgentFilter('');
    setSeniorFilter('');
    setPage(1);
  };

  // ★ 2026-08-02 — Export CSV (nome/cognome/email/telefono) — solo per
  // Morena/Claudia/Richard/Eufemia. Riusa gli stessi filtri della lista corrente.
  const toggleStage = useCallback((stageKey) => {
    setFilters((prev) => {
      const stages = prev.stages || [];
      const idx = stages.indexOf(stageKey);
      const next = idx >= 0
        ? [...stages.slice(0, idx), ...stages.slice(idx + 1)]
        : [...stages, stageKey];
      return { ...prev, stages: next };
    });
  }, []);

  const clearStages = useCallback(() => {
    setFilters((prev) => ({ ...prev, stages: [] }));
  }, []);

  const exportCsv = useCallback(async () => {
    if (!canExportCsv) return;
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch) params.set('branch', filters.branch);
      if (filters.source) params.set('source', filters.source);
      if (filters.stages && filters.stages.length > 0) {
        params.set('stage', filters.stages.join(','));
      }
      if (filters.search) params.set('search', filters.search);
      if (filters.startDate) params.set('start_date', filters.startDate);
      if (filters.endDate) params.set('end_date', filters.endDate);
      if (filters.startDate || filters.endDate) {
        params.set('date_field', dateMode === 'origin' ? 'created_at' : 'activity');
      }
      if (agentFilter) params.set('agent_id', agentFilter);
      if (seniorFilter) params.set('senior_id', seniorFilter);

      const url = `${API_BASE}/leads/export/priority.csv?${params.toString()}`;
      const token = getToken();
      const resp = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const msg = resp.status === 403
          ? t('leads.csvExportForbidden', "Non hai i permessi per l'export CSV.")
          : t('leads.csvExportError', 'Errore export CSV') + ` (${resp.status})`;
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      // Recupera filename dal Content-Disposition, altrimenti usa un default.
      const cd = resp.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      link.download = match ? match[1] : `contatti_lead_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(t('leads.csvDownloaded', 'CSV scaricato con successo'));
    } catch (err) {
      logger.error('export csv failed', err);
      toast.error(err?.message || t('leads.csvExportError', 'Errore export CSV'));
    } finally {
      setExportingCsv(false);
    }
  }, [
    canExportCsv, filters.branch, filters.source, filters.stages, filters.search,
    filters.startDate, filters.endDate, dateMode, agentFilter, seniorFilter, t,
  ]);

  // ─── Pre-compute lookup of selected lead objects (for preview in dialog)
  const selectedPreview = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return leads.filter((l) => selectedIds.has(l.id)).slice(0, 5);
  }, [selectedIds, leads]);

  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.post('/leads/_bulk-delete', {
        ids: Array.from(selectedIds),
      });
      // ★ 2026-08-11 (Fase 37) — Soft-delete: lead spostati nel cestino
      const moved = r.data?.moved_to_trash ?? r.data?.deleted ?? 0;
      const skipped = r.data?.skipped || 0;
      toast.success(
        t('leads.movedToTrash', '{{count}} lead spostati nel cestino · recuperabili per 30 giorni', { count: moved }) +
        (skipped ? ` (${t('leads.alreadyInTrash', 'già presenti nel cestino', { count: skipped })})` : ''),
      );
      setDeleteOpen(false);
      clearSelection();
      await load();
    } catch (err) {
      logger.debug('bulk delete failed', err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((d) => d.msg).join(' · ') : t('leads.deleteError', 'Errore eliminazione'));
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="leads-title">{t('leads.title', 'Lead')}</h1>
          <p className="text-xs text-muted-foreground">
            <span data-testid="leads-total-count">{total}</span> {t('leads.totalLeads', 'lead totali')}
            {leads.length > 0 && total > PAGE_SIZE && (
              <span className="ml-1">
                · {t('leads.shown', 'mostrati {{from}}–{{to}}', { from: (page - 1) * PAGE_SIZE + 1, to: (page - 1) * PAGE_SIZE + leads.length })}
                <span className="text-muted-foreground/70"> {t('leads.of', 'di {{total}}', { total })}</span>
              </span>
            )}
            {selectedIds.size > 0 && (
              <span className="ml-2 text-[var(--gp-gold)] font-medium">
                · {t('leads.selected', '{{count}} selezionati', { count: selectedIds.size })}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === 'admin' && <CrmFullExportButton />}
          {canExportCsv && (
            <Button
              size="sm"
              variant="outline"
              onClick={exportCsv}
              disabled={exportingCsv}
              data-testid="leads-export-csv-button"
              title={t('leads.exportCsvTitle', 'Scarica CSV: nome, cognome, email, cellulare (rispetta i filtri attivi)')}
            >
              <Download className="w-4 h-4 mr-1" />
              {exportingCsv ? t('leads.exporting', 'Esportazione…') : t('leads.exportCsv', 'Esporta CSV contatti')}
            </Button>
          )}
          <Button size="sm" onClick={() => nav('/app/leads/new')} data-testid="leads-new-button"><Plus className="w-4 h-4 mr-1" /> {t('leads.newLead', 'Nuovo lead')}</Button>
        </div>
      </div>

      <Card className="border-border">
        <div className="p-3 border-b border-border flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('leads.filters.search', 'Cerca per nome, email o telefono')}
              className="pl-8"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              data-testid="leads-search-input"
            />
          </div>
          <Select value={filters.branch || 'all'} onValueChange={(v) => setFilters({ ...filters, branch: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[180px]" data-testid="leads-branch-filter"><SelectValue placeholder={t('leads.branch', 'Branch')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('leads.allBranches', 'Tutti i rami')}</SelectItem>
              <SelectItem value="pre_branch">{t('leads.branchPre', 'Pre-branch')}</SelectItem>
              <SelectItem value="equity_estate">{t('leads.branchEquity', 'Equity Estate')}</SelectItem>
              <SelectItem value="real_estate">{t('leads.branchReal', 'Real Estate')}</SelectItem>
            </SelectContent>
          </Select>
          {/* ★ 2026-08-02 — Filtro stage MULTI-SELECT (Popover + Checkbox).
              Permette di combinare più fasi (es. Show + Proposte Inviate). */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-[220px] h-9 justify-between font-normal"
                data-testid="leads-stage-filter"
              >
                <span className="truncate text-sm">
                  {filters.stages.length === 0
                    ? t('leads.pipelineStage', 'Fase pipeline')
                    : filters.stages.length === 1
                      ? (pipelineStages.find((s) => s.key === filters.stages[0])?.short
                        || pipelineStages.find((s) => s.key === filters.stages[0])?.label
                        || filters.stages[0])
                      : t('leads.stagesSelected', '{{count}} fasi selezionate', { count: filters.stages.length })}
                </span>
                <ChevronDown className="w-4 h-4 opacity-60 shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start" data-testid="leads-stage-filter-menu">
              <div className="p-2 border-b border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">
                  {filters.stages.length > 0 ? t('leads.stagesSelectedShort', '{{count}} selezionate', { count: filters.stages.length }) : t('leads.selectStages', 'Seleziona fasi')}
                </span>
                {filters.stages.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[11px]"
                    onClick={clearStages}
                    data-testid="leads-stage-filter-clear"
                  >
                    <X className="w-3 h-3 mr-1" /> {t('leads.clear', 'Pulisci')}
                  </Button>
                )}
              </div>
              <div className="max-h-[320px] overflow-y-auto py-1">
                {pipelineStages.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground italic">{t('leads.loadingStages', 'Caricamento fasi…')}</div>
                ) : (
                  pipelineStages.map((s) => {
                    const checked = filters.stages.includes(s.key);
                    return (
                      <label
                        key={s.key}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[hsl(var(--accent))] transition-colors"
                        data-testid={`leads-stage-option-${s.key}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleStage(s.key)}
                          data-testid={`leads-stage-checkbox-${s.key}`}
                          aria-label={s.label}
                        />
                        <span className="text-sm flex-1 truncate">{s.short || s.label}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {/* ★ 2026-07-29: filtro Senior — utile per un Senior che cerca i propri lead */}
          <Select value={seniorFilter || 'all'} onValueChange={(v) => setSeniorFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[200px]" data-testid="leads-senior-filter">
              <SelectValue placeholder={t('leads.senior', 'Senior')} />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <SelectItem value="all">{t('leads.allSeniors', 'Tutti i Senior')}</SelectItem>
              {seniorAgents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.source || 'all'} onValueChange={(v) => setFilters({ ...filters, source: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[180px]" data-testid="leads-source-filter"><SelectValue placeholder={t('leads.filters.source', 'Sorgente')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('leads.allSources', 'Tutte le sorgenti')}</SelectItem>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="secondary" size="sm" onClick={applyFilters} data-testid="leads-refresh"><RefreshCcw className="w-4 h-4 mr-1" /> {t('leads.apply', 'Applica')}</Button>
        </div>

        {/* ★ 2026-07-03: Row 2 filters — date range + reset */}
        <div className="px-3 py-2 border-b border-border flex flex-wrap gap-2 items-center bg-[var(--surface-2)]/40">
          <CalendarRange className="w-3.5 h-3.5 text-muted-foreground" />
          <Label className="text-xs text-muted-foreground">
            {dateMode === 'origin' ? t('leads.createdFrom', 'Creato da') : t('leads.workedFrom', 'Lavorato da')}
          </Label>
          <Input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="h-8 w-36 text-xs"
            data-testid="leads-start-date"
          />
          <Label className="text-xs text-muted-foreground">{t('leads.to', 'a')}</Label>
          <Input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="h-8 w-36 text-xs"
            data-testid="leads-end-date"
          />
          <div className="h-6 w-px bg-border mx-1" />
          <DateModeSelector value={dateMode} onChange={setDateMode} size="sm" testIdPrefix="leads-date-mode" />
          <div className="flex gap-1 items-center ml-1">
            {[
              { label: t('leads.days7', '7g'), days: 7 },
              { label: t('leads.days30', '30g'), days: 30 },
              { label: t('leads.days90', '90g'), days: 90 },
            ].map((q) => (
              <Button
                key={q.days}
                size="sm"
                variant="ghost"
                className="h-7 text-[11px] px-2"
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - q.days);
                  setFilters({
                    ...filters,
                    startDate: start.toISOString().slice(0, 10),
                    endDate: end.toISOString().slice(0, 10),
                  });
                  setPage(1);
                  // load will be triggered by useEffect(page) OR applyFilters
                  setTimeout(applyFilters, 0);
                }}
                data-testid={`leads-quick-${q.days}d`}
              >
                {t('leads.last', 'Ultimi')} {q.label}
              </Button>
            ))}
          </div>
          <div className="ml-auto flex gap-2 items-center">
            {agentFilter && (
              <div
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-[11px] border border-blue-200"
                data-testid="leads-agent-chip"
              >
                <span className="font-medium">{t('leads.agentFilterActive', 'Filtro agente attivo')}</span>
                <button
                  type="button"
                  onClick={() => { setAgentFilter(''); setPage(1); }}
                  className="ml-1 hover:bg-blue-200 rounded p-0.5"
                  aria-label={t('leads.removeAgentFilter', 'Rimuovi filtro agente')}
                  data-testid="leads-agent-chip-remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {(filters.startDate || filters.endDate || filters.branch || filters.source || (filters.stages && filters.stages.length > 0) || filters.search || agentFilter) && (
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={resetFilters} data-testid="leads-reset-filters">
                <X className="w-3 h-3 mr-1" /> {t('leads.resetFilters', 'Reset filtri')}
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-[var(--surface-2)] border-b border-border text-left">
              <tr>
                {isAdmin && (
                  <th className="px-3 py-2 w-[36px]">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
                      onCheckedChange={toggleAll}
                      data-testid="leads-select-all"
                      aria-label={t('leads.selectAll', 'Seleziona tutti i lead')}
                    />
                  </th>
                )}
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.name', 'Nome')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.branch', 'Branch')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.score', 'Score')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.source', 'Sorgente')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.assignedTo', 'Assegnato a')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.tag', 'Tag')}</th>
                <th className="px-3 py-2 font-medium text-xs text-muted-foreground">{t('leads.cols.updated', 'Aggiornato')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isAdmin ? 8 : 7} className="px-3 py-8 text-center text-muted-foreground">{t('leads.loading', 'Caricamento…')}</td></tr>}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 7} className="px-3 py-10 text-center text-muted-foreground">{t('leads.empty', 'Nessun lead trovato.')}</td></tr>
              )}
              {leads.map((l) => {
                const cf = l.custom_fields || {};
                const assocName = cf.assigned_associate_name;
                const seniorName = cf.senior_agent_name;
                const isSelected = selectedIds.has(l.id);
                return (
                <tr
                  key={l.id}
                  className={`border-b border-border cursor-pointer ${isSelected ? 'bg-amber-50/60' : 'hover:bg-[hsl(var(--accent))]'}`}
                  onClick={(e) => {
                    // Prevent click-through when toggling the checkbox cell.
                    if (e.target.closest('[data-row-checkbox]')) return;
                    nav(`/app/leads/${l.id}`);
                  }}
                  data-testid={`leads-row-${l.id}`}
                >
                  {isAdmin && (
                    <td
                      className="px-3 py-2.5"
                      data-row-checkbox
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleOne(l.id)}
                        data-testid={`leads-row-${l.id}-checkbox`}
                        aria-label={t('leads.selectRow', 'Seleziona {{name}}', { name: l.name })}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{l.name}</div>
                    <div className="text-[11px] text-muted-foreground">{l.email || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5"><BranchPill branch={l.branch} /></td>
                  <td className="px-3 py-2.5"><ScoreBadge score={l.score} /></td>
                  <td className="px-3 py-2.5 text-xs">
                    {SOURCE_LABELS[l.source] || l.source}
                    {(l.funnel_code || l.custom_fields?.funnel_code) && (
                      <span
                        className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--gp-gold)]/15 text-[var(--gp-forest,#0C3C2D)] border border-[var(--gp-gold)]/40 uppercase tracking-wider"
                        data-testid={`leads-row-${l.id}-funnel`}
                      >
                        {l.funnel_code || l.custom_fields?.funnel_code}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5" data-testid={`leads-row-${l.id}-assigned`}>
                    <div className="flex flex-col gap-0.5 min-w-[140px]">
                      {assocName ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {assocName}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">{t('leads.noAssociate', '— no Associate —')}</span>
                      )}
                      {seniorName ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {seniorName}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">{t('leads.noSenior', '— no Senior —')}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(l.tags || []).slice(0, 3).map((t) => <TagChip key={t} tagKey={t} />)}
                      {(l.tags || []).length > 3 && <span className="text-[10px] text-muted-foreground">+{l.tags.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtRelative(l.updated_at || l.created_at)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ★ 2026-07-03: Paginazione — 50 per pagina */}
        {total > 0 && (
          <div className="p-3 border-t border-border flex items-center justify-between gap-3 flex-wrap" data-testid="leads-pagination">
            <div className="text-xs text-muted-foreground">
              {t('leads.pagination.page', 'Pagina')} <b className="text-foreground" data-testid="leads-current-page">{page}</b> {t('leads.pagination.of', 'di')}{' '}
              <b className="text-foreground" data-testid="leads-total-pages">{totalPages}</b>
              <span className="mx-1">·</span>
              <span data-testid="leads-shown-range">
                {leads.length > 0
                  ? `${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + leads.length}`
                  : '0'}
              </span>
              <span className="mx-1">/</span>
              <span>{total}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                data-testid="leads-page-first"
                title={t('leads.pagination.first', 'Prima pagina')}
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                data-testid="leads-page-prev"
                title={t('leads.pagination.prev', 'Precedente')}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Input
                type="number"
                min="1"
                max={totalPages}
                value={page}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setPage(n);
                }}
                className="h-8 w-16 text-xs text-center"
                data-testid="leads-page-input"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                data-testid="leads-page-next"
                title={t('leads.pagination.next', 'Successiva')}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                data-testid="leads-page-last"
                title={t('leads.pagination.last', 'Ultima pagina')}
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ── Floating bulk-actions bar (admin only, when ≥1 selected) ─── */}
      {isAdmin && selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-border shadow-2xl rounded-xl px-4 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3"
          data-testid="leads-bulk-bar"
        >
          <span className="text-sm font-medium text-foreground">
            {selectedIds.size === 1
              ? t('leads.bulkBar.one', '{{count}} lead selezionato', { count: selectedIds.size })
              : t('leads.bulkBar.other', '{{count}} lead selezionati', { count: selectedIds.size })}
          </span>
          <span className="w-px h-6 bg-border" />
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSelection}
            data-testid="leads-bulk-clear"
            className="h-8 text-xs"
          >
            <X className="w-3 h-3 mr-1" /> {t('leads.bulkBar.cancel', 'Annulla')}
          </Button>
          <Button
            size="sm"
            onClick={() => setDeleteOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
            data-testid="leads-bulk-delete-button"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('leads.bulkBar.deleteSelected', 'Elimina selezionati')}
          </Button>
        </div>
      )}

      {/* ── Bulk delete confirmation dialog ──────────────────────────── */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="leads-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {t('leads.deleteDialog.title', 'Spostare {{count}} lead nel cestino?', { count: selectedIds.size })}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm">
                {t('leads.deleteDialog.descriptionPrefix', 'Stai per spostare')} <b>{selectedIds.size}</b> {t('leads.deleteDialog.descriptionSuffix', 'lead nel cestino. Non appariranno più nelle liste, pipeline e KPI.')}
                {selectedPreview.length > 0 && (
                  <ul className="list-disc list-inside mt-2 text-xs space-y-0.5 max-h-32 overflow-auto">
                    {selectedPreview.map((l) => (
                      <li key={l.id}>{l.name} <span className="text-muted-foreground">({l.email || l.phone || t('leads.deleteDialog.noContact', 'no contact')})</span></li>
                    ))}
                    {selectedIds.size > selectedPreview.length && (
                      <li className="text-muted-foreground italic">
                        {t('leads.deleteDialog.andOthers', '… e altri {{count}} lead', { count: selectedIds.size - selectedPreview.length })}
                      </li>
                    )}
                  </ul>
                )}
                <div
                  className="mt-3 p-2.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(t(
                      'leads.deleteDialog.recoveryInfo',
                      '♻️ Potrai ripristinarli entro <b>30 giorni</b> dalla sezione <b>Cestino</b>. Dopo questo periodo verranno cancellati definitivamente insieme a incassi, pagamenti, ordini proposte e tutti i dati collegati.',
                    )),
                  }}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leads-bulk-delete-cancel">{t('leads.deleteDialog.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={deleting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="leads-bulk-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting
                ? t('leads.deleteDialog.moving', 'Sposto nel cestino…')
                : t('leads.deleteDialog.confirm', 'Sposta {{count}} nel cestino', { count: selectedIds.size })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
